import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import styles from "./Header.module.css";
import logo from "./Logoras.webp";
import { FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { BsInstagram } from "react-icons/bs";
import { MdEmail } from "react-icons/md";
import { FiDownload, FiChevronDown, FiArrowRight } from "react-icons/fi";
import { services } from "../../data/services";
import { mailtoLink, trackLead, LeadChannel } from "../../data/contact";

type SocialLink = {
  icon: React.ReactNode;
  link: string;
  name: string;
  /** Only set on links that can produce a lead. */
  channel?: LeadChannel;
};

type NavItem = {
  label: string;
  href: string;
  /** Present on items that open a dropdown panel. */
  children?: { label: string; href: string }[];
};

const navItems: NavItem[] = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  {
    label: "Our Services",
    href: "/services",
    children: services.map((s) => ({ label: s.name, href: `/services/${s.slug}` })),
  },
  { label: "Products", href: "/products" },
  { label: "Gallery", href: "/gallery" },
  { label: "Contact", href: "/contact" },
];

const socialLinks: SocialLink[] = [
  { icon: <FaFacebookF />, link: "https://facebook.com", name: "Facebook" },
  { icon: <BsInstagram />, link: "https://instagram.com/ras_advertising_gifts", name: "Instagram" },
  { icon: <FaLinkedinIn />, link: "https://linkedin.com", name: "LinkedIn" },
  {
    icon: <MdEmail />,
    link: mailtoLink("Enquiry from your website", "Header", "Hi Rashed Al Shamsi,\n\n"),
    name: "Email",
    channel: "email" as LeadChannel,
  },
];

// Company profile / product catalogue (served from /public)
const CATALOGUE_URL = `${process.env.PUBLIC_URL}/Rashed-Al-Shamsi-Catalogue.pdf`;

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  /** Which desktop dropdown is open (by label), or null. */
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  /** Which mobile-drawer accordion is expanded (by label), or null. */
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Hover uses a short close delay so the pointer can travel from the parent
  // link down into the panel without the menu snapping shut.
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setOpenMenu(null);
    setOpenAccordion(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, []);

  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  const openDropdown = (label: string) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };

  const scheduleClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 150);
  };

  const closeAll = () => {
    setMenuOpen(false);
    setOpenMenu(null);
    setOpenAccordion(null);
  };

  const isNavItemActive = (href: string) => {
    if (href === "/") {
      return location.pathname === "/" && !location.hash;
    }

    if (href.startsWith("/#")) {
      const [path, hash] = href.split("#");
      return location.pathname === path && location.hash === `#${hash}`;
    }

    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ""}`}>
      {/* Left: Logo */}
      <div className={styles.logoContainer}>
        <Link to="/" className={styles.logoLink}>
          <img src={logo} alt="Rashed Al Shamsi Advertising Gifts Logo" className={styles.logoImg} />
        </Link>
      </div>

      {/* Center: Navigation */}
      <nav className={`${styles.nav} ${menuOpen ? styles.open : ""}`} aria-label="Primary">
        <button
          className={styles.drawerClose}
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          ×
        </button>
        <ul className={styles.navList}>
          {navItems.map((item) =>
            item.children ? (
              <li
                key={item.label}
                className={`${styles.hasMenu} ${openMenu === item.label ? styles.menuOpen : ""} ${
                  openAccordion === item.label ? styles.accordionOpen : ""
                }`}
                onMouseEnter={() => openDropdown(item.label)}
                onMouseLeave={scheduleClose}
              >
                <div className={styles.parentRow}>
                  <Link
                    to={item.href}
                    className={isNavItemActive(item.href) ? styles.activeLink : ""}
                    onClick={closeAll}
                  >
                    {item.label}
                  </Link>
                  <button
                    type="button"
                    className={styles.caret}
                    onClick={() => {
                      setOpenMenu((v) => (v === item.label ? null : item.label));
                      setOpenAccordion((v) => (v === item.label ? null : item.label));
                    }}
                    aria-expanded={openMenu === item.label || openAccordion === item.label}
                    aria-haspopup="true"
                    aria-label={`${item.label} menu`}
                  >
                    <FiChevronDown className={styles.chev} />
                  </button>
                </div>

                <div className={styles.dropdown} role="menu">
                  <Link to={item.href} className={styles.dropdownAll} role="menuitem" onClick={closeAll}>
                    All {item.label}
                    <FiArrowRight size={14} />
                  </Link>
                  <ul className={styles.dropdownGrid}>
                    {item.children.map((child) => (
                      <li key={child.href}>
                        <Link
                          to={child.href}
                          role="menuitem"
                          className={location.pathname === child.href ? styles.dropdownActive : ""}
                          onClick={closeAll}
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ) : (
              <li key={item.label}>
                <Link
                  to={item.href}
                  className={isNavItemActive(item.href) ? styles.activeLink : ""}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </Link>
              </li>
            )
          )}
        </ul>

        {/* Catalogue button inside drawer (mobile) */}
        <a
          href={CATALOGUE_URL}
          download
          className={styles.drawerCatalogue}
          onClick={() => setMenuOpen(false)}
        >
          <FiDownload /> Download Catalogue
        </a>
      </nav>

      {/* Right: Catalogue + Social icons + Mobile hamburger */}
      <div className={styles.rightSection}>
        <a href={CATALOGUE_URL} download className={styles.catalogueBtn}>
          <FiDownload className={styles.catalogueIcon} />
          <span>Catalogue</span>
        </a>

        <div className={styles.socials}>
          {socialLinks.map((s) => (
            <a
              key={s.name}
              href={s.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.name}
              onClick={() => s.channel && trackLead(s.channel, "Header")}
            >
              {s.icon}
            </a>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          className={`${styles.hamburger} ${menuOpen ? styles.active : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={styles.bar}></span>
          <span className={styles.bar}></span>
          <span className={styles.bar}></span>
        </button>
      </div>

      {/* Backdrop */}
      {menuOpen && <div className={styles.backdrop} onClick={() => setMenuOpen(false)} />}
    </header>
  );
};

export default Header;
